import { SINGLETON_ENDPOINT_ID, TENANT_ID, TENANT_NAME } from './governedMcpConstants';
import type { DashboardSource } from './dashboardAnalytics';
import {
  EXTERNAL_MCP_CONNECTIONS,
  SEED_CLIENTS,
  SEED_POLICIES,
  SEED_PUBLISHED,
} from './governedMcpSynthetic';
import type {
  ActivityDecision,
  ActivityEvent,
  GatewayApiKey,
  QuotaAssignment,
  QuotaDirectory,
  QuotaDirectoryUser,
  QuotaUsage,
} from './governedMcpTypes';
import { currentQuotaMonth } from './quotaUsage';

const FALLBACK_PEOPLE: QuotaDirectoryUser[] = [
  { id: 'u-priya', name: 'Priya Shah', email: 'priya.shah@acme.financial', roleId: 'r-lead', roleName: 'Lead', departmentId: 'd-risk' },
  { id: 'u-marcus', name: 'Marcus Chen', email: 'marcus.chen@acme.financial', roleId: 'r-ops', roleName: 'Operations', departmentId: 'd-claims' },
  { id: 'u-elena', name: 'Elena Rossi', email: 'elena.rossi@acme.financial', roleId: 'r-counsel', roleName: 'Counsel', departmentId: 'd-legal' },
  { id: 'u-james', name: 'James Okonkwo', email: 'james.okonkwo@acme.financial', roleId: 'r-analyst', roleName: 'Analyst', departmentId: 'd-kyc' },
  { id: 'u-aisha', name: 'Aisha Rahman', email: 'aisha.rahman@acme.financial', roleId: 'r-lead', roleName: 'Lead', departmentId: 'd-data' },
  { id: 'u-daniel', name: 'Daniel Park', email: 'daniel.park@acme.financial', roleId: 'r-pm', roleName: 'Product', departmentId: 'd-product' },
  { id: 'u-sofia', name: 'Sofia Alvarez', email: 'sofia.alvarez@acme.financial', roleId: 'r-ops', roleName: 'Operations', departmentId: 'd-claims' },
  { id: 'u-noah', name: 'Noah Williams', email: 'noah.williams@acme.financial', roleId: 'r-eng', roleName: 'Engineer', departmentId: 'd-platform' },
];

const FALLBACK_DEPARTMENTS = [
  { id: 'd-risk', name: 'Risk', matchIds: ['d-risk'] },
  { id: 'd-claims', name: 'Claims', matchIds: ['d-claims'] },
  { id: 'd-legal', name: 'Legal', matchIds: ['d-legal'] },
  { id: 'd-kyc', name: 'KYC', matchIds: ['d-kyc'] },
  { id: 'd-data', name: 'Data Science', matchIds: ['d-data'] },
  { id: 'd-product', name: 'Product', matchIds: ['d-product'] },
  { id: 'd-platform', name: 'Platform', matchIds: ['d-platform'] },
];

const FALLBACK_ROLES = [
  { id: 'r-lead', name: 'Lead' },
  { id: 'r-ops', name: 'Operations' },
  { id: 'r-counsel', name: 'Counsel' },
  { id: 'r-analyst', name: 'Analyst' },
  { id: 'r-pm', name: 'Product' },
  { id: 'r-eng', name: 'Engineer' },
];

const MODEL_MIX: [string, number][][] = [
  [['Claude 3.5 Sonnet', 0.46], ['GPT-4o', 0.32], ['Internal Agent', 0.22]],
  [['GPT-4o', 0.54], ['Claude 3.5 Sonnet', 0.28], ['Gemini 2.0 Flash', 0.18]],
  [['Gemini 2.0 Flash', 0.48], ['GPT-4o', 0.33], ['Internal Agent', 0.19]],
  [['Internal Agent', 0.44], ['Claude 3.5 Sonnet', 0.31], ['GPT-4o', 0.25]],
];

function hash(value: string): number {
  let next = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    next ^= value.charCodeAt(i);
    next = Math.imul(next, 16777619);
  }
  return next >>> 0;
}

function unit(seed: string): number {
  return (hash(seed) % 10000) / 10000;
}

function isoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function lastDates(days: number): string[] {
  const today = new Date();
  const rows: string[] = [];
  for (let index = days - 1; index >= 0; index -= 1) {
    rows.push(isoDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - index)));
  }
  return rows;
}

function mergeById<T extends { id: string }>(live: T[], extra: T[]): T[] {
  const seen = new Set(live.map((item) => item.id));
  const added = extra.filter((item) => !seen.has(item.id));
  return live.concat(added);
}

function pick<T>(items: T[], seed: string): T {
  return items[hash(seed) % items.length];
}

function decisionFor(seed: string): ActivityDecision {
  const roll = hash(seed) % 100;
  if (roll < 82) return 'allowed';
  if (roll < 90) return 'denied';
  if (roll < 96) return 'approval_required';
  return 'failed';
}

function eventsPerDay(dayIndex: number, totalDays: number): number {
  const recency = 1 - dayIndex / Math.max(totalDays - 1, 1);
  if (recency > 0.92) return 14;
  if (recency > 0.67) return 8;
  return 4;
}

function buildDirectory(live: QuotaDirectory): QuotaDirectory {
  const departments = live.departments.length ? live.departments : FALLBACK_DEPARTMENTS;
  const roles = live.roles.length ? live.roles : FALLBACK_ROLES;
  const users = mergeById(live.users, FALLBACK_PEOPLE).map((user, index) => {
    const department = departments.find((item) => item.id === user.departmentId) || departments[index % departments.length];
    const role = roles.find((item) => item.id === user.roleId) || roles[index % roles.length];
    return {
      ...user,
      departmentId: user.departmentId || department?.id || '',
      roleId: user.roleId || role?.id || '',
      roleName: user.roleName || role?.name || '',
    };
  });
  return { users, roles, departments };
}

function buildActivity(source: {
  users: QuotaDirectoryUser[];
  clients: DashboardSource['clients'];
  published: DashboardSource['published'];
}): ActivityEvent[] {
  const tools = source.published.length ? source.published : SEED_PUBLISHED;
  const clients = source.clients.length ? source.clients : SEED_CLIENTS;
  const users = source.users.length ? source.users : FALLBACK_PEOPLE;
  const days = lastDates(90);
  const events: ActivityEvent[] = [];
  days.forEach((day, dayIndex) => {
    const count = eventsPerDay(dayIndex, days.length);
    for (let slot = 0; slot < count; slot += 1) {
      const seed = `syn-act-${day}-${slot}`;
      const user = pick(users, `${seed}-user`);
      const client = pick(clients, `${seed}-client`);
      const tool = pick(tools, `${seed}-tool`);
      const decision = decisionFor(seed);
      const hour = 7 + (hash(`${seed}-h`) % 12);
      const minute = hash(`${seed}-m`) % 60;
      const durationMs = Math.round(90 + unit(`${seed}-lat`) * 720 + (decision === 'failed' ? 180 : 0));
      events.push({
        id: `syn-act-${day}-${slot}`,
        timestamp: `${day}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00.000Z`,
        requestId: `syn-req-${hash(seed).toString(16)}`,
        tenant: TENANT_NAME,
        userId: user.id,
        userName: user.name,
        groupNames: [],
        roles: user.roleName ? [user.roleName] : [],
        clientId: client.id,
        clientName: client.name,
        endpointId: SINGLETON_ENDPOINT_ID,
        endpointName: 'Selfic MCP',
        operation: 'invoke_tool',
        capabilityId: tool.id,
        capabilityName: tool.mcpToolName || tool.sourceName,
        capabilityKind: tool.capabilityKind,
        representations: tool.mcpRepresentations,
        inputSummary: 'Sample governed MCP request.',
        policiesEvaluated: [],
        decision,
        approvalStatus: decision === 'approval_required' ? 'pending' : decision === 'denied' ? 'not_applicable' : 'not_required',
        dataSources: [tool.sourceName],
        classificationsEncountered: [tool.dataClassification],
        recordsAccessed: 1 + (hash(`${seed}-rec`) % 4),
        fieldsMasked: [],
        workflowRef: null,
        outputClassification: tool.dataClassification,
        durationMs,
        result: decision === 'allowed' ? 'Completed.' : decision === 'denied' ? 'Blocked by policy.' : decision === 'failed' ? 'Tool error.' : 'Held for approval.',
        error: decision === 'failed' ? 'execution_failed' : decision === 'denied' ? 'authorization_denied' : null,
        trace: [],
      });
    }
  });
  return events;
}

function buildQuotaBundle(source: {
  users: QuotaDirectoryUser[];
  clients: DashboardSource['clients'];
}): { apiKeys: GatewayApiKey[]; assignments: QuotaAssignment[]; usages: QuotaUsage[] } {
  const clients = source.clients.length ? source.clients : SEED_CLIENTS;
  const users = source.users.length ? source.users : FALLBACK_PEOPLE;
  const month = currentQuotaMonth();
  const dates = lastDates(90);
  const apiKeys: GatewayApiKey[] = [];
  const assignments: QuotaAssignment[] = [];
  const usages: QuotaUsage[] = [];
  const now = new Date().toISOString();

  users.forEach((user, personIndex) => {
    const clientCount = 1 + (hash(`${user.id}-clients`) % Math.min(2, Math.max(clients.length, 1)));
    for (let slot = 0; slot < clientCount; slot += 1) {
      const client = clients[(personIndex + slot * 3) % clients.length];
      const seed = hash(`${user.id}-${client.id}`);
      const tokenLimit = [400_000, 750_000, 1_000_000, 1_500_000, 2_000_000, 3_000_000][seed % 6];
      const usedRatio = 0.22 + ((seed >> 4) % 90) / 100;
      const tokensUsed = Math.round(tokenLimit * Math.min(1.12, usedRatio));
      const mix = MODEL_MIX[seed % MODEL_MIX.length];
      const mixTotal = mix.reduce((sum, [, share]) => sum + share, 0);
      const weights = dates.map((_, index) => {
        const wave = 0.55 + 0.45 * Math.sin((index + (seed % 9)) * 0.42);
        return wave * (0.7 + ((seed >> (index % 8)) % 5) * 0.08);
      });
      const weightTotal = weights.reduce((sum, weight) => sum + weight, 0) || 1;
      const scopeRoll = seed % 10;
      const scope = scopeRoll < 3 ? 'user' : scopeRoll < 6 ? 'role' : scopeRoll < 8 ? 'department' : 'user';
      const scopeId = scope === 'role' ? user.roleId || user.id : scope === 'department' ? user.departmentId || user.id : user.id;
      apiKeys.push({
        id: `syn-key-${user.id}-${client.id}`,
        kind: 'user',
        name: user.name,
        description: 'Sample dashboard key',
        secret: '',
        username: user.email,
        userId: user.id,
        email: user.email,
        tenantId: TENANT_ID,
        details: '',
        clientId: client.id,
        endpointId: SINGLETON_ENDPOINT_ID,
        status: 'active',
        lastUsedAt: now,
        createdAt: now,
        updatedAt: now,
        createdBy: 'Selfic',
      });
      assignments.push({
        id: `syn-quota-${scope}-${scopeId}-${client.id}-${month}`,
        scope,
        scopeId,
        clientId: client.id,
        month,
        tokenLimit,
        status: 'active',
        name: user.name,
        renewsMonthly: false,
        resourceType: 'client',
        version: 1,
        updatedAt: now,
      });
      usages.push({
        id: `syn-usage-${user.id}-${client.id}`,
        userId: user.id,
        clientId: client.id,
        tokensUsed,
        dailyUsage: dates.map((date, index) => ({
          date,
          tokens: Math.max(0, Math.round((weights[index] / weightTotal) * tokensUsed)),
        })),
        byModel: mix.map(([model, share]) => ({
          model,
          tokens: Math.round((share / mixTotal) * tokensUsed),
        })),
        updatedAt: now,
      });
    }
  });

  return { apiKeys, assignments, usages };
}

export function enrichDashboardSource(live: DashboardSource): DashboardSource {
  const clients = live.clients.length ? live.clients : SEED_CLIENTS;
  const published = live.published.length ? live.published : SEED_PUBLISHED;
  const policies = live.policies.length ? live.policies : SEED_POLICIES;
  const quotaDirectory = buildDirectory(live.quotaDirectory);
  const syntheticActivity = buildActivity({ users: quotaDirectory.users, clients, published });
  const quota = buildQuotaBundle({ users: quotaDirectory.users, clients });
  const activity = mergeById(live.activity, syntheticActivity);
  const apiKeys = mergeById(live.apiKeys, quota.apiKeys);
  const quotaAssignments = mergeById(live.quotaAssignments, quota.assignments);
  const quotaUsages = mergeById(live.quotaUsages, quota.usages);
  const externalMcp = live.externalMcp.length ? live.externalMcp : EXTERNAL_MCP_CONNECTIONS;

  return {
    activity,
    published,
    clients,
    policies,
    apiKeys,
    quotaAssignments,
    quotaUsages,
    quotaDirectory,
    externalMcp,
  };
}
